import { useCallback, useEffect, useState } from "react";
import { mappingApi } from "@services/mappingApi";
import { ApiError } from "@utils/httpClient";
import type {
  JobViewModel,
  MappingJobProgressNotification,
} from "@models/job";
import { useJobNotifications } from "@hooks/useJobNotifications";

export interface UseJobDetailsResult {
  job: JobViewModel | null;
  jobLoading: boolean;
  jobError: string | null;
  clearJobError: () => void;
  /** Bumped whenever the job status changes, so result queries re-run. */
  jobVersion: number;
  fetchJob: () => Promise<void>;
}

/**
 * Owns the job entity: initial fetch plus the live SignalR subscription
 * that streams status changes and row-progress updates.
 */
export const useJobDetails = (
  jobId: string | undefined,
): UseJobDetailsResult => {
  const [job, setJob] = useState<JobViewModel | null>(null);
  const [jobLoading, setJobLoading] = useState(false);
  const [jobError, setJobError] = useState<string | null>(null);
  const [jobVersion, setJobVersion] = useState(0);

  const clearJobError = useCallback(() => setJobError(null), []);

  const applyJobStatus = useCallback((next: JobViewModel): void => {
    setJob(next);
    setJobVersion((v) => v + 1);
  }, []);

  const applyJobProgress = useCallback(
    (progress: MappingJobProgressNotification): void => {
      setJob((prev) => {
        if (!prev || prev.jobId !== progress.jobId) return prev;
        const rowsOk = Math.max(prev.rowsOk, progress.rowsOk);
        const rowsFailed = Math.max(prev.rowsFailed, progress.rowsFailed);
        const files = progress.fileIndex === undefined || !progress.sourceUnits
          ? prev.files
          : prev.files?.map((file) => file.fileIndex === progress.fileIndex
            ? { ...file, sourceUnits: progress.sourceUnits }
            : file);
        return {
          ...prev,
          rowsOk,
          rowsFailed,
          rowsTotal: progress.rowsTotal,
          files,
        };
      });
    },
    [],
  );

  const { ensureConnected, subscribe } = useJobNotifications(
    applyJobStatus,
    applyJobProgress,
  );

  const fetchJob = useCallback(async (): Promise<void> => {
    if (!jobId) return;
    setJobLoading(true);
    setJobError(null);
    try {
      const response = await mappingApi.getJob(jobId);
      setJob(response);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to load job";
      setJobError(message);
    } finally {
      setJobLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    if (!jobId) return;
    void fetchJob();
    void ensureConnected().catch(() => {});
    const unsubscribe = subscribe(jobId);
    return () => {
      unsubscribe();
    };
  }, [jobId, fetchJob, ensureConnected, subscribe]);

  return { job, jobLoading, jobError, clearJobError, jobVersion, fetchJob };
};
